import invariant from 'tiny-invariant'
import { MeetingHandlingDiscriminator } from '@chilipiper/api-client/src/distribution'
import {
  PublicRoutingInfoResponse,
  SanitizedAssigneeSelection,
  ScheduleMeetingRequestPublic,
  SelectRouteResponse,
} from '@chilipiper/api-client/src/handoff'
import * as v from 'valibot'
import {
  AdminIndividualSchedulingLink,
  GroupSchedulingLink1,
  HandoffRouting,
  MeetingAttempt,
  MeetingAttemptResponse,
  MeetingAttendees,
  OwnershipSchedulingLink,
  PersonalIndividualSchedulingLink,
  ProductFeature,
  RoundRobinSchedulingLink,
} from '@chilipiper/api-client/src/user-availability'
import { MeetingAttendees as MeetingAttendeesRequest } from '@chilipiper/api-client/src/team-scheduling'
import {
  GuestFormDataFields,
  GuestFormMixed,
  MapString,
  MeetingLocation,
  PublicMeetingType,
  QuestionDataType,
} from '@chilipiper/api-client/src/meeting-types'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { OrderedListOfUsers } from '@chilipiper/distribution-components'
import {
  AdminSchedulingLink3,
  FilledDataFields,
  GuestFormData,
  GuestFormDataDiscriminator,
  PersonalSchedulingLink1,
  RoundRobinSchedulingLink1,
} from '@chilipiper/api-client/src/team-scheduling'
import { AssigneeOptions } from './types'

export const getAssigneeOptions = (
  meetingAttempt?: MeetingAttempt,
  handoffRouting?: PublicRoutingInfoResponse,
  distribution?: OrderedListOfUsers,
  distributionId?: string,
  isRescheduling?: boolean
): AssigneeOptions => {
  if (
    meetingAttempt?.productFeature.type === 'AdminIndividualSchedulingLink' ||
    meetingAttempt?.productFeature.type === 'PersonalIndividualSchedulingLink'
  ) {
    return {
      combinedAvailability: true,
      hostId: meetingAttempt.productFeature.hostId,
      isReservationFlow: true,
    }
  }

  if (meetingAttempt?.productFeature.type === 'HandoffRouting' && handoffRouting) {
    if (handoffRouting.type !== 'PublicRoutingInfoForSelected') {
      return
    }

    if (handoffRouting.selectResponse.selectedAssignee.type === 'User') {
      return {
        combinedAvailability: true,
        hostId: handoffRouting.selectResponse.selectedAssignee.id,
        isReservationFlow: true,
      }
    }

    if (handoffRouting.selectResponse.selectedAssignee.type === 'UserFromDistribution') {
      return {
        combinedAvailability: true,
        hostId: handoffRouting.selectResponse.selectedAssignee.user,
        isReservationFlow: true,
      }
    }

    if (handoffRouting.selectResponse.selectedAssignee.type === 'FromDistribution') {
      return getRoundRobinAssigneesInfo(
        handoffRouting.selectResponse.selectedAssignee.algorithm as MeetingHandlingDiscriminator,
        distributionId,
        handoffRouting.selectResponse.selectedAssignee.users,
        handoffRouting.selectResponse.selectedAssignee.vacations
      )
    }

    throw new Error('invalid handoff routing select response')
  }

  if (meetingAttempt?.productFeature.type === 'RoundRobinSchedulingLink' && distribution) {
    return getRoundRobinAssigneesInfo(
      (() => {
        if (isRescheduling) {
          if (distribution.reassignmentType === 'AnyTeamMember') {
            return 'Flexible'
          }
          return 'Strict'
        }
        return distribution.roundRobinKind
      })(),
      distributionId,
      distribution.users,
      distribution.vacations
    )
  }
}

const getRoundRobinAssigneesInfo = (
  roundRobinKind: MeetingHandlingDiscriminator,
  distributionId?: string,
  allUsers?: string[],
  vacations?: string[]
): AssigneeOptions => {
  if (!allUsers) {
    return {
      combinedAvailability: false,
      isReservationFlow: false,
      distributionId: distributionId ?? '',
      assigneeIds: [],
    }
  }

  if (roundRobinKind === 'Flexible') {
    return {
      combinedAvailability: false,
      isReservationFlow: false,
      distributionId: distributionId ?? '',
      assigneeIds: getFlexibleRoundRobinAssigneesList(allUsers, vacations),
    }
  }

  const filteredOutVacationUsers = allUsers?.filter(user => !vacations?.includes(user)) ?? []
  if (filteredOutVacationUsers[0]) {
    return {
      combinedAvailability: true,
      isReservationFlow: true,
      hostId: filteredOutVacationUsers[0],
    }
  }
  // If it's RR and all users are OFF
  // we display availability of all distribution users
  return {
    combinedAvailability: false,
    isReservationFlow: false,
    distributionId: distributionId ?? '',
    assigneeIds: allUsers || [],
  }
}

const getFlexibleRoundRobinAssigneesList = (
  allUsers?: string[],
  vacations?: string[]
): string[] => {
  const filteredOutVacationUsers = allUsers?.filter(user => !vacations?.includes(user)) ?? []
  if (filteredOutVacationUsers.length > 0) {
    return filteredOutVacationUsers
  }
  // If it's RR and all users are OFF
  // we display availability of all distribution users
  return allUsers || []
}
export const mapQuestionDataTypeToDataFieldDataType = (
  questionDataType: QuestionDataType
): DataFieldDataType => {
  switch (questionDataType.type) {
    case 'QuestionCheckboxes':
      return {
        type: 'Checkbox',
        values: questionDataType.values,
      }
    case 'QuestionEmail':
      return {
        type: 'Email',
      }

    case 'QuestionMultiLineText':
      return { type: 'LongText' }
    case 'QuestionPhone':
      return {
        type: 'Phone',
      }
    case 'QuestionPicklist':
      return { type: 'Picklist', values: questionDataType.values }
    case 'QuestionRadioButtons':
      return { type: 'RadioButton', values: questionDataType.values }
    case 'QuestionSingleLineText':
      return { type: 'ShortText' }
    case 'QuestionTrueFalse':
      return { type: 'TrueFalse' }
  }
}
export const getHandoffWithReservationPayload = ({
  reservationId,
  assigneeId,
  timezone,
  location,
}: {
  assigneeId?: string
  location?: MeetingLocation
  reservationId: string
  timezone?: string
}) => {
  invariant(location, 'Location not found')
  invariant(assigneeId, 'Assignee not found')
  invariant(location.type !== 'GoToMeetingLink', 'GoToMeetingLink is not supported here yet')

  const payload: ScheduleMeetingRequestPublic = {
    type: 'ScheduleByReservation',
    assigneeId,
    reservationId,
    guestTimezone: timezone,
    location,
    secondaryFeature: ['SuggestedTimes'],
  }

  return payload
}
export const getHandoffStrictAssigneeId = (selectedAssignee?: SanitizedAssigneeSelection) => {
  if (selectedAssignee?.type === 'User') {
    return selectedAssignee.id
  }

  if (selectedAssignee?.type === 'UserFromDistribution') {
    return selectedAssignee.user
  }
}
export const getGuestEmail = (
  productFeature?: ProductFeature,
  handoffRouting?: PublicRoutingInfoResponse
) => {
  if (!productFeature) {
    return
  }
  if (
    productFeature.type === 'AdminIndividualSchedulingLink' ||
    productFeature.type === 'PersonalIndividualSchedulingLink' ||
    productFeature.type === 'RoundRobinSchedulingLink'
  ) {
    return productFeature.primaryGuestV2?.email
  }
  if (
    productFeature.type === 'HandoffRouting' &&
    handoffRouting?.type === 'PublicRoutingInfoForSelected'
  ) {
    return handoffRouting.primaryGuest.email
  }
  return undefined
}

export const getMeetingRequestFeature = ({
  productFeature,
  reservationId,
  distribution,
  distributionId,
  assigneesIds,
  duration,
  slotStartAt,
}: FeaturePayload): RequestFeature | undefined => {
  if (productFeature.type === 'AdminIndividualSchedulingLink' && reservationId) {
    const adminSchedulingLinkFeature: FeatureAdminSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: productFeature?.hostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      linkId: productFeature.linkId,
      type: 'AdminSchedulingLink',
    }

    return adminSchedulingLinkFeature
  }

  if (productFeature.type === 'PersonalIndividualSchedulingLink' && reservationId) {
    const personalSchedulingLinkFeature: FeaturePersonalSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: productFeature?.hostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      meetingTypeId: productFeature.meetingTypeId,
      type: 'PersonalSchedulingLink',
    }

    return personalSchedulingLinkFeature
  }

  if (
    productFeature.type === 'RoundRobinSchedulingLink' &&
    distribution?.roundRobinKind === 'Strict' &&
    distribution?.users?.[0] &&
    reservationId &&
    distributionId
  ) {
    const roundRobinSingleAssignee: FeatureRoundRobinSchedulingLinkRequest = {
      timeAndAssignment: {
        assigneeId: distribution?.users[0],
        host: {
          type: 'Assignee',
        },
        distributionId,
        reservationId,
        type: 'WithSingleAssigneeDistribution',
      },
      linkId: productFeature.linkId,
      type: 'RoundRobinSchedulingLink',
    }

    return roundRobinSingleAssignee
  }

  if (
    productFeature.type === 'RoundRobinSchedulingLink' &&
    (distribution || assigneesIds) &&
    productFeature.selectedMeetingTypeId &&
    slotStartAt &&
    duration &&
    distributionId
  ) {
    const roundRobinMultipleAssignees: FeatureRoundRobinSchedulingLinkRequest = {
      timeAndAssignment: {
        meetingTypeReference: {
          meetingTypeId: productFeature.selectedMeetingTypeId,
          timestamp: new Date().toISOString(),
        },
        interval: {
          startsAt: slotStartAt,
          duration,
        },
        availableAssignees: distribution
          ? getFlexibleRoundRobinAssigneesList(distribution.users, distribution.vacations)
          : (assigneesIds ?? []),
        host: {
          type: 'Assignee',
        },
        distributionId,
        type: 'WithMultipleAssigneesDistribution',
      },
      linkId: productFeature.linkId,
      type: 'RoundRobinSchedulingLink',
    }

    return roundRobinMultipleAssignees
  }
}

export const getSuggestedTimeRouting = (routing?: PublicRoutingInfoResponse) => {
  if (
    routing?.type === 'PublicRoutingInfoForSelected' &&
    routing.selectResponse.type === 'SuggestedTimesCreated'
  ) {
    return routing.selectResponse
  }

  return
}

export const getMeetingAttemptReservationPayload = ({
  meetingAttempt,
  slotStartsAt,
  distribution,
  routing,
  meetingTypeRef,
}: {
  distribution?: OrderedListOfUsers
  meetingAttempt?: MeetingAttemptResponse
  meetingTypeRef: {
    id: string
    timestamp: string
  }
  routing?: SelectRouteResponse
  slotStartsAt: string
}) => {
  if (meetingAttempt?.type === 'Available') {
    const payloadBase = {
      slotStartsAt,
      meetingTypeRef,
      durationOverride: meetingAttempt.attempt.meetingInvite.meetingDurationOverride,
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'AdminIndividualSchedulingLink' ||
      meetingAttempt.attempt.productFeature.type === 'PersonalIndividualSchedulingLink'
    ) {
      return {
        ...payloadBase,
        userIds: [meetingAttempt.attempt.productFeature.hostId],
      }
    }

    if (meetingAttempt.attempt.productFeature.type === 'HandoffRouting') {
      let userIds: string[] = []

      if (routing?.selectedAssignee.type === 'User') {
        userIds = [routing.selectedAssignee.id]
      }

      if (routing?.selectedAssignee.type === 'UserFromDistribution') {
        userIds = [routing.selectedAssignee.user]
      }

      if (
        distribution?.roundRobinKind === 'Strict' &&
        routing?.selectedAssignee.type === 'FromDistribution' &&
        routing.selectedAssignee.users[0]
      ) {
        userIds = [routing.selectedAssignee.users[0]]
      }

      if (
        distribution?.roundRobinKind === 'Flexible' &&
        routing?.selectedAssignee.type === 'FromDistribution'
      ) {
        userIds = routing.selectedAssignee.users
      }

      return {
        ...payloadBase,
        userIds,
      }
    }
  }

  return
}

type RequestFeature =
  | FeatureAdminSchedulingLinkRequest
  | FeatureRoundRobinSchedulingLinkRequest
  | FeaturePersonalSchedulingLinkRequest

type FeaturePayload = {
  productFeature:
    | AdminIndividualSchedulingLink
    | PersonalIndividualSchedulingLink
    | RoundRobinSchedulingLink
    | HandoffRouting
    | GroupSchedulingLink1
    | OwnershipSchedulingLink
  reservationId?: string
  distributionId?: string
  slotStartAt?: string
  duration?: string
  assigneesIds?: string[]
  distribution?: OrderedListOfUsers
}
export const mapMeetingAttendees = (attendees?: MeetingAttendees): MeetingAttendeesRequest => {
  if (!attendees) {
    return {
      type: 'NoAdditionalAttendees',
    }
  }
  return attendees.type === 'SomeAttendees'
    ? {
        ...attendees,
        attendees: attendees.attendees.map(attendee => ({
          ...attendee,
          responseStatus: 'NeedsAction',
        })),
      }
    : attendees
}
export const getRoundRobinSchedulingLinkId = (
  meetingAttempt: MeetingAttemptResponse | undefined
) => {
  if (
    meetingAttempt?.type === 'Available' &&
    meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink'
  ) {
    return meetingAttempt.attempt.productFeature.linkId
  }
  return undefined
}

type FeatureAdminSchedulingLinkRequest = Pick<
  AdminSchedulingLink3,
  'timeAndAssignment' | 'linkId' | 'type'
>

type FeatureRoundRobinSchedulingLinkRequest = Pick<
  RoundRobinSchedulingLink1,
  'timeAndAssignment' | 'linkId' | 'type'
>

type FeaturePersonalSchedulingLinkRequest = Pick<
  PersonalSchedulingLink1,
  'timeAndAssignment' | 'meetingTypeId' | 'type'
>

type FeatureAdminSchedulingLinkRequestWithGuestFormData = FeatureAdminSchedulingLinkRequest &
  Pick<AdminSchedulingLink3, 'guestFormData'>

type FeatureRoundRobinSchedulingLinkRequestWithGuestFormData =
  FeatureRoundRobinSchedulingLinkRequest & Pick<RoundRobinSchedulingLink1, 'guestFormData'>

type FeaturePersonalSchedulingLinkRequestWithGuestFormData = FeaturePersonalSchedulingLinkRequest &
  Pick<PersonalSchedulingLink1, 'guestFormData'>

type RequestFeatureWithGuestFormData =
  | FeatureAdminSchedulingLinkRequestWithGuestFormData
  | FeatureRoundRobinSchedulingLinkRequestWithGuestFormData
  | FeaturePersonalSchedulingLinkRequestWithGuestFormData

export const getMeetingRequestFeatureWithGuestFormData = (
  feature: RequestFeature,
  formValues: GuestFormValues,
  meetingType: PublicMeetingType
): RequestFeatureWithGuestFormData => {
  if (feature.type === 'PersonalSchedulingLink') {
    return {
      ...feature,
      guestFormData: {
        fieldValues: {},
        questionValues: {},
        ...getGuestFormData<GuestFormData>(formValues, meetingType),
      },
    }
  }

  return {
    ...feature,
    guestFormData: getGuestFormData<FilledDataFields>(formValues, meetingType),
  }
}

const FORM_TYPE: Record<string, GuestFormDataDiscriminator> = {
  DataFields: 'FilledDataFields',
  Mixed: 'FilledMixed',
}

export type GuestFormValues = {
  [key: string]: string | string[]
}

const getGuestFormData = <T = GuestFormData>(
  formValues: GuestFormValues,
  meetingType?: PublicMeetingType
): T => {
  const formType = FORM_TYPE[meetingType?.guestForm.type ?? ''] ?? 'FilledMixed'

  const formDataValues = Object.keys(formValues).reduce((result, key) => {
    const field = formValues[key]
    const value = Array.isArray(field) ? field.join(',') : field

    if (!value) {
      return result
    }

    return {
      ...result,
      [key]: value,
    }
  }, {} as MapString)

  if (meetingType?.guestForm.type !== 'Mixed') {
    return {
      values: formDataValues,
      type: formType,
    } as T
  }

  return Object.keys(formDataValues).reduce(
    (result, key) => {
      if (isMixedGuestForm(meetingType.guestForm)) {
        const field = meetingType?.guestForm.items.find(
          field =>
            (field.type === 'Question' && field.id === key) ||
            (field.type === 'Field' && field.reference === key)
        )

        const typeKey = field?.type === 'Question' ? 'questionValues' : 'fieldValues'

        return {
          ...result,
          [typeKey]: {
            ...result[typeKey],
            [key]: formDataValues[key],
          },
        }
      }

      return result
    },
    {
      fieldValues: {},
      questionValues: {},
      type: formType,
    }
  ) as T
}

const isMixedGuestForm = (
  guestForm?: GuestFormDataFields | GuestFormMixed
): guestForm is GuestFormMixed => guestForm?.type === 'Mixed'

export const ErrorSchema = v.object({
  error: v.object({
    errorCode: v.string(),
    errorMessage: v.string(),
  }),
})
